import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { LoadingScreen } from "@/components/LoadingScreen";
import { MAIN_CONTENT_ID } from "@/components/public-layout";
import { useSession } from "@/hooks/auth-hooks";
import { db } from "@/lib/instantdb";

export const Route = createFileRoute("/setup")({
	component: SetupPage,
});

type McpKeySummary = {
	configured: boolean;
	keyPrefix: string | null;
	lastUsedAt: string | null;
	name: string | null;
	updatedAt: string | null;
};

export function SetupPage() {
	const navigate = useNavigate();
	const { user, isPending } = useSession();

	useEffect(() => {
		if (isPending || user) return;
		window.location.replace("/login?redirect=/setup");
	}, [user, isPending]);

	const { data: userData } = db.useQuery(
		user?.id ? { users: { $: { where: { id: user.id } } } } : null,
	);
	const dbUser = userData?.users?.[0];

	const mcpKeyQuery = useQuery({
		enabled: !!user,
		queryFn: async () => {
			const res = await fetch("/api/mcp/key", {
				headers: { Accept: "application/json" },
			});
			if (!res.ok) throw new Error("Failed to fetch MCP status");
			return res.json() as Promise<{ key: McpKeySummary }>;
		},
		queryKey: ["dashboard", "mcp-key"],
		refetchInterval: 10_000,
	});

	const isMcpConnected =
		import.meta.env.DEV ||
		Boolean(
			dbUser?.agentLastSeenAt ||
				mcpKeyQuery.data?.key?.lastUsedAt ||
				mcpKeyQuery.data?.key?.configured,
		);

	useEffect(() => {
		if (mcpKeyQuery.isLoading) return;
		if (isMcpConnected) {
			navigate({ to: "/dashboard", replace: true });
		}
	}, [isMcpConnected, mcpKeyQuery.isLoading, navigate]);

	if (isPending || !user) {
		return <LoadingScreen />;
	}

	return (
		<main
			id={MAIN_CONTENT_ID}
			className="flex min-h-svh items-center justify-center"
			style={{ backgroundColor: "#0a0a0a" }}
		>
			<div
				className="absolute top-0 left-0 right-0 h-0.5"
				style={{
					background: "linear-gradient(to right, #6B3FF2, #5A33D4, #6B3FF2)",
				}}
			/>
			<SetupContent />
		</main>
	);
}

const COMMAND = "npx agentframer@latest init";

const LLM_INSTRUCTIONS = `Set up AgentFramer for me. Run this command:

${COMMAND}

Follow the prompts to complete the setup. After the setup is complete, restart your current session so the MCP server becomes available.`;

function useCopyToClipboard(text: string) {
	const [copied, setCopied] = useState(false);

	const copy = useCallback(async () => {
		await navigator.clipboard.writeText(text);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	}, [text]);

	return { copied, copy };
}

function SetupContent() {
	return (
		<div className="mx-auto max-w-md space-y-8 px-4 sm:px-6">
			<div className="space-y-2 text-center">
				<p
					className="text-[10px] font-bold uppercase tracking-[0.1em] mb-1"
					style={{ color: "#6B3FF2", fontFamily: "inherit" }}
				>
					Getting Started
				</p>
				<h1
					className="text-4xl font-semibold tracking-tight"
					style={{
						color: "#fcfcfc",
						fontFamily: "'Geist', -apple-system, sans-serif",
					}}
				>
					Set up AgentFramer
				</h1>
				<p className="text-sm" style={{ color: "#888888" }}>
					Choose how you'd like to connect your AI agent.
				</p>
			</div>

			<div className="space-y-3">
				<AiAssistantCard />
				<TerminalCard />
			</div>

			<div className="space-y-4">
				<div
					className="flex items-center justify-center gap-3 text-sm"
					style={{ color: "#c8c6c4" }}
				>
					<span className="relative flex size-2" aria-hidden="true">
						<span
							className="absolute inline-flex size-full animate-ping rounded-full opacity-75"
							style={{ backgroundColor: "#2b9a66" }}
						/>
						<span
							className="relative inline-flex size-2 rounded-full"
							style={{ backgroundColor: "#2b9a66" }}
						/>
					</span>
					Waiting for connection
				</div>
			</div>
		</div>
	);
}

function AiAssistantCard() {
	const { copied, copy } = useCopyToClipboard(LLM_INSTRUCTIONS);

	return (
		<button
			type="button"
			onClick={copy}
			aria-label={
				copied ? "Instructions copied!" : "Copy AI assistant instructions"
			}
			className="group flex w-full items-center gap-4 p-5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2b9a66]/60"
			style={{
				border: "1px solid #2b9a66",
				borderTop: "3px solid #2b9a66",
				backgroundColor: "rgba(43,154,102,0.03)",
			}}
			onMouseEnter={(e) => {
				(e.currentTarget as HTMLElement).style.backgroundColor =
					"rgba(43,154,102,0.06)";
			}}
			onMouseLeave={(e) => {
				(e.currentTarget as HTMLElement).style.backgroundColor =
					"rgba(43,154,102,0.03)";
			}}
			onFocus={(e) => {
				(e.currentTarget as HTMLElement).style.backgroundColor =
					"rgba(43,154,102,0.06)";
			}}
			onBlur={(e) => {
				(e.currentTarget as HTMLElement).style.backgroundColor =
					"rgba(43,154,102,0.03)";
			}}
		>
			<div
				className="flex size-10 shrink-0 items-center justify-center"
				style={{ backgroundColor: "rgba(43,154,102,0.1)" }}
			>
				<span
					className="material-symbols-outlined"
					aria-hidden="true"
					style={{
						fontSize: "20px",
						color: "#2b9a66",
						fontVariationSettings: "'FILL' 1",
					}}
				>
					smart_toy
				</span>
			</div>
			<div className="min-w-0 flex-1">
				<div className="flex items-center gap-2">
					<h2 className="text-sm font-semibold" style={{ color: "#ffffff" }}>
						Give to your AI assistant
					</h2>
					<span
						className="px-2 py-0.5 text-[11px] font-bold"
						style={{
							backgroundColor: "rgba(43,154,102,0.1)",
							border: "1px solid #2b9a66",
							color: "#2b9a66",
						}}
					>
						Recommended
					</span>
				</div>
				<p className="mt-0.5 text-sm" style={{ color: "#888888" }}>
					Copy instructions for Claude, ChatGPT, or Cursor
				</p>
			</div>
			<div className="shrink-0 transition-colors" style={{ color: "#888888" }}>
				{copied ? (
					<span
						className="material-symbols-outlined"
						aria-hidden="true"
						style={{
							fontSize: "16px",
							color: "#2b9a66",
							fontVariationSettings: "'FILL' 1",
						}}
					>
						check_circle
					</span>
				) : (
					<span
						className="material-symbols-outlined"
						aria-hidden="true"
						style={{ fontSize: "16px" }}
					>
						content_copy
					</span>
				)}
			</div>
		</button>
	);
}

function TerminalCard() {
	const { copied, copy } = useCopyToClipboard(COMMAND);

	return (
		<button
			type="button"
			onClick={copy}
			aria-label={copied ? "Command copied!" : "Copy terminal command"}
			className="group flex w-full items-center gap-4 p-5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
			style={{
				border: "1px solid rgba(255,255,255,0.08)",
				borderTop: "3px solid #6B3FF2",
				backgroundColor: "transparent",
			}}
			onMouseEnter={(e) => {
				(e.currentTarget as HTMLElement).style.backgroundColor = "#131313";
			}}
			onMouseLeave={(e) => {
				(e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
			}}
			onFocus={(e) => {
				(e.currentTarget as HTMLElement).style.backgroundColor = "#131313";
			}}
			onBlur={(e) => {
				(e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
			}}
		>
			<div
				className="flex size-10 shrink-0 items-center justify-center"
				style={{ backgroundColor: "rgba(255,255,255,0.04)" }}
			>
				<span
					className="material-symbols-outlined"
					aria-hidden="true"
					style={{ fontSize: "20px", color: "#888888" }}
				>
					terminal
				</span>
			</div>
			<div className="min-w-0 flex-1">
				<h2 className="text-sm font-semibold" style={{ color: "#ffffff" }}>
					Run manually in terminal
				</h2>
				<p className="mt-0.5 font-mono text-xs" style={{ color: "#888888" }}>
					$ {COMMAND}
				</p>
			</div>
			<div className="shrink-0 transition-colors" style={{ color: "#888888" }}>
				{copied ? (
					<span
						className="material-symbols-outlined"
						aria-hidden="true"
						style={{
							fontSize: "16px",
							color: "#2b9a66",
							fontVariationSettings: "'FILL' 1",
						}}
					>
						check_circle
					</span>
				) : (
					<span
						className="material-symbols-outlined"
						aria-hidden="true"
						style={{ fontSize: "16px" }}
					>
						content_copy
					</span>
				)}
			</div>
		</button>
	);
}
